

import { useState, useMemo, useCallback, useEffect } from "react";

import StepLayout from "./StepLayout";
import NavFooter from "./NavFooter";
import HumanBody from "../HumanBody";

import styles from "./SelectSymptoms.module.css";

import {
  COMMON_SYMPTOMS,
  LOCATION_CATEGORIES,
  BODY_PARTS,
  ALL_SYMPTOM_LABELS,
} from "./SelectSymptomsConstants";

import {
  useVoiceSymptoms,
  matchHealthKeywords,
  addSymptom,
  removeSymptom,
  toggleSymptom,
} from "./SelectSymptomsUtils";

import {
  BottomPanel,
  SidePanel,
  LocationCard,
  BodyPartPill,
  VoiceStatus,
} from "./SelectSymptomsComponents";

import { bodyPartSymptoms } from "./const";
import PatientCard from "./PatientCard";
import useCurrentLocation from "../../hooks/useCurrentLocation";

/* ══════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════ */
export default function StepSelectSymptoms({
  initial,
  patient,
  onNext,
  onBack,
  selectedSymptoms,
  setSelectedSymptoms,
  currentlocation
}) {
  /* ── state ── */
  const [tab, setTab] = useState("quick");
  const [selected, setSelected] = useState(initial || []);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [showAllCommon, setShowAllCommon] = useState(false);


    const { latitude, longitude, state, district, loading, error } = currentlocation;
  const storedHistory = JSON.parse(localStorage.getItem("medicalHistory") || "{}");


  useEffect(() => {
    if (selected) {
      localStorage.setItem("selectedSymptoms", JSON.stringify(selected));
    }
  }, [selected]);

  // Desktop: right column panel (View All category)
  const [sidePanel, setSidePanel] = useState(null);

  // Bottom panel: used for zone click (both) + View All on mobile
  const [bottomPanel, setBottomPanel] = useState(null);

  const [voiceBanner, setVoiceBanner] = useState([]);
  const [activeBodyPart, setActiveBodyPart] = useState(null);

  /* ── voice ── */
  const handleDetected = useCallback(transcript => {
    const matched = matchHealthKeywords(transcript);
    if (!matched.length) return;
    matched.forEach(m =>
      setSelected(prev => addSymptom(prev, m.label))
    );
    setVoiceBanner(matched.map(m => m.label));
    setTimeout(() => setVoiceBanner([]), 3000);
  }, []);

  const { isListening, liveText, micError, toggleMic, supported } =
    useVoiceSymptoms({ onDetected: handleDetected });

  /* ── search ── */
  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    return ALL_SYMPTOM_LABELS
      .filter(x => x.toLowerCase().includes(search.toLowerCase()))
      .slice(0, 7);
  }, [search]);

  /* ── helpers ── */
  const doAdd = useCallback(lbl => setSelected(prev => addSymptom(prev, lbl)), []);
  const doRemove = useCallback(lbl => setSelected(prev => removeSymptom(prev, lbl)), []);
  const doToggle = useCallback(lbl => setSelected(prev => toggleSymptom(prev, lbl)), []);

  /* Detect mobile (≤768px) to decide whether to use bottom panel or side panel for View All */
  const isMobile = () =>
    typeof window !== "undefined" && window.innerWidth <= 768;

  /* View All click: desktop → side panel, mobile → bottom panel */
  const handleViewAll = useCallback(cat => {
    if (isMobile()) {
      setBottomPanel({ label: cat.label, sub: "Select all that apply", symptoms: cat.symptoms });
    } else {
      setSidePanel(cat);
    }
  }, []);

  const openBodyZone = useCallback(partId => {
    if (!partId) return;

    const id = partId.replace("body-model-", "");
    const matched = bodyPartSymptoms?.find(item => item.id === id);

    if (!matched) return;

    const panelData = {
      id,
      label: matched.label || id.charAt(0).toUpperCase() + id.slice(1),
      symptoms: (matched.symptoms || []).map(s => s.label || s),
    };

    if (isMobile()) {
      setBottomPanel({
        ...panelData,
        sub: "Tap to select symptoms",
      });
    } else {
      setSidePanel(panelData);
    }
  }, []);

  const handlePillClick = useCallback(partId => {
    setActiveBodyPart(prev => (prev === partId ? null : partId));

    const matched = bodyPartSymptoms?.find(item => item.id === partId);
    if (!matched) return;

    const panelData = {
      id: partId,
      label: matched.label || partId.charAt(0).toUpperCase() + partId.slice(1),
      symptoms: (matched.symptoms || []).map(s => s.label || s),
    };

    if (isMobile()) {
      setBottomPanel({
        ...panelData,
        sub: "Tap to select symptoms",
      });
    } else {
      setSidePanel(panelData);
    }
  }, []);

  const handleCheckboxChange = useCallback((e, _i, label, itemId) => {
    setSelectedSymptoms?.(prev =>
      e.target.checked
        ? prev.some(v => v.id === itemId) ? prev : [...prev, { name: label, id: itemId }]
        : prev.filter(v => v.id !== itemId)
    );
  }, [setSelectedSymptoms]);

  const handleViewAllSymptoms = useCallback(() => {
    const allCat = {
      id: "all",
      label: "All Symptoms",
      symptoms: LOCATION_CATEGORIES.flatMap(c => c.symptoms),
    };

    if (isMobile()) {
      setBottomPanel({
        ...allCat,
        sub: "Select all that apply",
      });
    } else {
      setSidePanel(allCat);
    }
  }, []);

  

  const mapPreScreenToHistory = (data) => {
    if (!data) return {};

    return {
      condition: data.hasCondition === "Yes" ? data.diagnosed || true : false,
      takingMeds: data.takingMeds === "Yes",
      allergy: data.hasAllergy,
      surgery: data.hasSurgery === "Yes" ? data.surgeryWhen || true : false,
    };
  };

  const historyData = mapPreScreenToHistory(storedHistory) || patient?.history;

  const switchTab = t => {
    setTab(t);
    setSidePanel(null);
    setActiveBodyPart(null);
  };

  const visibleCommon = showAllCommon ? COMMON_SYMPTOMS : COMMON_SYMPTOMS.slice(0, 38);

  /* ── voice status props (shared) ── */
  const voiceStatusProps = { isListening, liveText, voiceBanner, micError, styles };

  /* ══════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════ */
  return (
    <StepLayout
      title="What symptoms are you experiencing?"
      subtitle="Search or use voice input to select symptoms. You can select multiple symptoms."
      icon="🔍"
      noPad
      // patient={patient}
      footer={
        <NavFooter
          onBack={onBack}
          onNext={() => {
            localStorage.setItem("selectedSymptoms", JSON.stringify(selected));
            onNext(selected);
          }}
          nextLabel="Continue"
          disableNext={selected.length === 0}
        />
      }
    >
      {/* ══ ROOT ══ */}
      <div className={styles.sssRoot}>

        {/* ── LEFT ── */}
        <div className={styles.sssLeft}>
          <div className={styles.sssSearchRow}>
            <div className={styles.sssSearchBox}>


              <svg
                className={styles.sssSearchIco}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>

              <input
                type="text"
                value={isListening ? liveText : search}
                onChange={e => { setSearch(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                placeholder={isListening ? "Listening for symptoms…" : "Search symptoms…"}
                className={`${styles.sssSearchInput} ${isListening ? styles.sssSearchListen : ""}`}
                readOnly={isListening}
              />

              {/* Clear button */}
              {search && !isListening && (
                <button
                  className={styles.sssSearchClear}
                  onClick={() => setSearch("")}
                >
                  ×
                </button>
              )}

              <button
                className={`${styles.sssVoiceInside} ${isListening ? styles.sssVoiceInsideOn : ""}`}
                onMouseDown={(e) => e.preventDefault()} // prevent blur
                onClick={toggleMic}
                disabled={!supported}
                title={supported ? (isListening ? "Stop" : "Speak symptoms") : "Not supported"}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                </svg>
              </button>

              {/* Dropdown */}
              {searchOpen && searchResults.length > 0 && (
                <div className={styles.sssDropdown}>
                  {searchResults.map(sym => (
                    <button
                      key={sym}
                      onMouseDown={() => {
                        doAdd(sym);
                        setSearch("");
                        setSearchOpen(false);
                      }}
                      className={styles.sssDropItem}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                      {sym}
                    </button>
                  ))}
                </div>
              )}
              {isListening && (
                <div className={styles.sssDropdownVoice}>
                  <div className={styles.sssVoiceDesktop}>
                    <VoiceStatus {...voiceStatusProps} />
                  </div>
                </div>
              )}



            </div>
          </div>


          <div className={styles.sssVoiceMobile}>
            <VoiceStatus {...voiceStatusProps} />
          </div>

          {/* TABS */}
          <div className={styles.sssTabs}>
            <button
              className={`${styles.sssTab} ${tab === "quick" ? styles.sssTabOn : ""}`}
              onClick={() => switchTab("quick")}
            >
              Symptoms by Location
            </button>
            <button
              className={`${styles.sssTab} ${tab === "advanced" ? styles.sssTabOn : ""}`}
              onClick={() => switchTab("advanced")}
            >
              Symptoms by Women's Life Stages
            </button>
          </div>

          {/* ══ QUICK TAB ══ */}
          {tab === "quick" && (
            <div className={styles.sssQuickContent}>
              <div className={styles.sssAdvContent}>
                <div className={styles.sssCol}>
                  <div className={styles.sssSecHead}>
                    <span className={` ava-text-subheading `}>
                      Common Symptoms in  <span className="ava-text-md">{district ? `${district}, ` : ""}{state || ""}</span>
                    </span>

                    {COMMON_SYMPTOMS.length > 28 && (
                      <button
                        className={styles.sssViewAllBtn}
                        onClick={() => setShowAllCommon(v => !v)}
                      >
                        {showAllCommon ? "Show less ↑" : "View all ↓"}
                      </button>
                    )}
                  </div>

                  <div className={styles.sssChips}>
                    {visibleCommon.map(sym => {
                      const isSelected = selected.some(s => s.label === sym.label);
                      return (
                        <button
                          key={sym.id}
                          onClick={() => doToggle(sym.label)}
                          className={`${styles.sssChip} ${isSelected ? styles.sssChipOn : ""}`}
                        >
                          {isSelected
                            ? <span className={styles.sssTick}>✓</span>
                            : <div className={styles.sssChipIcon}>{sym.icon}</div>
                          }
                          {sym.label}
                        </button>
                      );
                    })}
                  </div>
                </div>


                {/* <div className={styles.sssCol}>
                    <div className={styles.sssSecHead}>
                      <span className={` ava-text-subheading `}>
                        Common Symptoms Based On Category
                      </span>
                    </div>

                    <div className={styles.sssLocGrid}>
                      {LOCATION_CATEGORIES.map(cat => (
                        <LocationCard
                          key={cat.id}
                          cat={cat}
                          selected={selected}
                          onViewAll={handleViewAll}
                        />
                      ))}
                    </div>
                  </div> */}

              </div>
            </div>
          )}

          {/* ══ ADVANCED TAB ══ */}
          {tab === "advanced" && (
            <>
              <div className={styles.sssAdvContent}>
                <div className={styles.sssSecHead}>
                  <span className={` ava-text-subheading `}>
                    Browse by Category
                  </span>
                </div>

                <div className={styles.sssLocGrid}>
                  {LOCATION_CATEGORIES.map(cat => (
                    <LocationCard
                      key={cat.id}
                      cat={cat}
                      selected={selected}
                      onViewAll={handleViewAll}
                    />
                  ))}
                </div>
              </div>
              {/* <div className={styles.sssAdvContent}>
                <div className={styles.sssAdvBodyRow}>
                  <div className={styles.sssAdvLeftCol}>
                    <div className={styles.sssAdvSecHead}>
                      <span className={styles.sssAdvSecLabel}>
                        Advanced Search by Body Part
                      </span>
                      <button
                        className={styles.sssAdvViewAllBtn}
                        onClick={handleViewAllSymptoms}
                      >
                        View all
                      </button>
                    </div>

                    <div className={styles.sssAdvBodyPartsList}>
                      {BODY_PARTS.map(part => (
                        <BodyPartPill
                          key={part.id}
                          part={part}
                          active={activeBodyPart === part.id}
                          onClick={handlePillClick}
                        />
                      ))}
                    </div>
                  </div>


                  <div className={styles.sssAdvRightCol}>
                    <p className={styles.sssBodyHint}>Tap a body area to select symptoms</p>
                    <div className={styles.sssBodyWrap}>
                      <HumanBody
                        selectedSymptoms={selectedSymptoms || []}
                        handleChange={handleCheckboxChange}
                        onBodyPartClick={openBodyZone}
                      />
                    </div>
                  </div>

                </div>
              </div> */}
            </>

          )}
        </div>

        {/* ── RIGHT PANEL (desktop side panel for View All) ── */}
        <div className={`${styles.sssRight} ${sidePanel ? styles.sssRightPanel : ""}`}>
          {sidePanel && (
            <SidePanel
              cat={sidePanel}
              selected={selected}
              onToggle={doToggle}
              onClose={() => setSidePanel(null)}
            />
          )}
          {!sidePanel && (
            <div className={styles.patientInfo}>
              <PatientCard patient={patient} />

              <div className="text-xs text-gray-500 flex items-center gap-2 mt-1 lg:hidden">
                {loading ? "Detecting..." : error ? "" : (
                  <>
                    <span>📍</span>
                    <span>{district ? `${district}, ` : ""}{state || ""}</span>
                  </>
                )}
              </div>
              <div className={styles.historyCard}>
                <h4 className={styles.historyTitle}>Medical History</h4>

                <ul className={styles.historyList}>
                  {Object.entries(historyData).length > 0 ? (
                    Object.entries(historyData).map(([key, value], i) => {
                      let label = "";
                      let icon = "?";

                      switch (key) {
                        case "condition":
                          label = value && value !== true ? `Diagnosed with ${value}` : "Has medical condition";
                          icon = value ? "✔" : "✖";
                          break;

                        case "takingMeds":
                          label = "Currently taking medications";
                          icon = value ? "✔" : "✖";
                          break;

                        case "allergy":
                          label = "Has allergies";
                          icon = value === "Yes" ? "✔" : value === "No" ? "✖" : "?";
                          break;

                        case "surgery":
                          label = value && value !== true ? `Surgery: ${value}` : "Past surgery";
                          icon = value ? "✔" : "✖";
                          break;

                        default:
                          label = key;
                      }

                      return (
                        <li key={i} className={styles.historyItem}>
                          <span className={styles.historyDot}>{icon}</span>
                          {label}
                        </li>
                      );
                    })
                  ) : (
                    <li className={styles.historyEmpty}>No history available</li>
                  )}
                </ul>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ══ SELECTED BAR ══ */}
      {selected.length > 0 && (
        <div className={styles.sssSelBar}>
          <div className={styles.sssSelLeft}>
            <span className={styles.sssSelCount}>{selected.length}</span>
            <div className={styles.sssSelChips}>
              {selected.map(s => (
                <span key={s.id} className={styles.sssSelChip}>
                  {s.label}
                  <button onClick={() => doRemove(s.label)} className={styles.sssSelRemove}>×</button>
                </span>
              ))}
            </div>
          </div>
          <button className={styles.sssClearBtn} onClick={() => setSelected([])}>Clear all</button>
        </div>
      )}

      {/* ══ BOTTOM PANEL (zone modal + mobile view-all) ══ */}
      <BottomPanel
        data={bottomPanel}
        selected={selected}
        onToggle={doToggle}
        onClose={() => setBottomPanel(null)}
      />
    </StepLayout>
  );
}

